import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchScanIfNeeded,
  requestScanSummary,
  resetAiScannerAnswers,
  startAiScannerSession
} from '../../actions/scan';
import { fetchSymbolIfNeeded } from '../../actions/symbol';
import HASHTAG_FILTER_CONFIG from '../../constants/hashtagFilterConfig';
import { getScansForSelection } from '../../constants/hashtagScanMap';
import INVESTMENT_QUESTIONS from '../../constants/investmentQuestions';
import SCORING_SCANS from '../../constants/scoringScans';
import closeIcon from '../../icons/Icon-AI-Scanner-Close.svg';
import aiScannerLogo from '../../icons/Icon-AI-Scanner-Logo.svg';
import {
  dismissAiScannerBar,
  isAiScannerBarDismissed
} from '../../utils/aiScannerBarDismissal';
import * as aiScannerSession from '../../utils/aiScannerSession';
import {
  filterSelectedHashtags,
  getRequiredScans
} from '../../utils/hashtagFilterEngine';
import { useFeatureToggle } from '../../utils/hooks/useFeatureToggle';
import useLocalize from '../../utils/hooks/useLocalize';
import useOnboardingSeen from '../../utils/hooks/useOnboardingSeen';
import {
  selectCandidates,
  selectCandidateSymbols
} from '../../utils/scanCandidates';
import {
  buildSearchRound,
  buildSummarizeRequest
} from '../../utils/summarizePayload';
import './AiScanner.scss';
import AiScannerCoachMark from './AiScannerCoachMark';
import HashtagChips, { HASHTAG_DEFINITIONS } from './HashtagChips';
import InvestmentQuestions from './InvestmentQuestions';
import ScanResults from './ScanResults';
import WelcomeBackScreen from './WelcomeBackScreen';

const BAR_FOOTER_GAP = 0;
const MOBILE_FOOTER_ID = 'gnavi-mobile-footer';
const SHEET_PEEK = 44; // visible height when collapsed (px)
const SHEET_TOP_MARGIN = 40; // gap from overlay top when expanded (px)

/**
 * AiScannerContainer Component
 *
 * Renders the final "銘柄ミッケ" bottom-bar entry point (STORY-022) at the
 * bottom of the Scanner page at the smartphone breakpoint. The bar shows the
 * 銘柄ミッケ logo, a subtitle and an ✕ button. Tapping the bar body opens the
 * AI Scanner chat overlay; the overlay can be dismissed via the ✕ icon at the
 * top-right.
 *
 * The bar floats fixed at the bottom of the viewport while the member scrolls.
 * Once the footer scrolls into view its `bottom` value is increased so it sits
 * directly on top of the footer instead of covering it, keeping the footer
 * fully visible and tappable at the end of the page.
 *
 * The ✕ hides the bar only (not the chat). The dismissal is remembered in a
 * cookie with a one-month expiry (see {@link dismissAiScannerBar}), so the bar
 * re-appears automatically once the cookie lapses.
 *
 * On the user's first open a two-step onboarding coach mark (see
 * {@link AiScannerCoachMark}) is layered on top of the main blank overlay.
 * The "seen" flag is persisted per user via {@link useOnboardingSeen} and is
 * only recorded when the user completes the onboarding (taps "AI Chatbot を
 * はじめる") or closes the overlay via ✕. Leaving mid-flow does not record
 * the flag, so the onboarding resumes from step 1 on the next visit.
 *
 * @returns {JSX.Element} The AI Scanner bottom bar and, when open, the overlay.
 */
export const AiScannerContainer = () => {
  const { isFeatureEnabled } = useFeatureToggle('aiScannerPopsicleEnabled');
  const { hasSeen, markSeen, userId } = useOnboardingSeen();
  const localize = useLocalize();
  const dispatch = useDispatch();
  const [isOpen, setIsOpen] = useState(false);
  const [barBottomOverride, setBarBottomOverride] = useState(null);
  // STORY-022: the bar is shown by default and stays until the member taps ✕.
  // The dismissal persists in a cookie (one-month expiry), read once on mount
  // so a returning member within the month never sees the bar re-appear.
  const [barDismissed, setBarDismissed] = useState(() => isAiScannerBarDismissed());
  // Locally track whether the coach mark is showing for the CURRENT open
  // session. This lets the "seen" flag be flipped in localStorage before we
  // unmount without immediately re-rendering the main chat mid-transition.
  const [showCoachMark, setShowCoachMark] = useState(false);
  const [sheetExpanded, setSheetExpanded] = useState(false);
  const [sheetOffset, setSheetOffset] = useState(null); // px from top while dragging; null = snapped
  const [sheetDragging, setSheetDragging] = useState(false);
  // STORY-009: true once the member taps 「NNN 件を表示」 in the hashtag sheet.
  const [showQuestions, setShowQuestions] = useState(false);
  // STORY (#725): which state the overlay body shows on open — the
  // welcome-back screen (a saved session with >=1 round exists) or today's
  // chat. Re-decided fresh on every `handleOpen`, never persisted.
  const [screen, setScreen] = useState('chat');
  // STORY (#725): set by `handleResume` from the session `handleOpen` already
  // loaded; rendered as read-only history pairs alongside the live chat.
  const [restoredRounds, setRestoredRounds] = useState(null);
  // STORY (#725): the session `handleOpen` loaded for the welcome-back
  // screen, stashed so `handleResume` doesn't need a second `load()` call.
  const loadedSessionRef = useRef(null);
  const sheetRef = useRef(null);
  const questionsRef = useRef(null);
  const resultsRef = useRef(null);
  // STORY (#725): a dedicated ref for "land on the latest restored round" —
  // deliberately NOT `resultsRef`. The Rule 5 effect below tests
  // `resultsRef.current` truthiness as a proxy for "the live results div has
  // mounted"; sharing it with a restored round (which mounts long before any
  // live results exist) would make that proxy permanently true and misdirect
  // every later scroll (e.g. selecting hashtags and reaching the questions
  // step) back to the old restored summary instead of the questions anchor.
  // Anchored to the round's own container (not its `ScanResults` div) so
  // `block: 'start'` lands on the round's hashtag condition line — the
  // 「#タグ」の条件で探しました✨ header — rather than scrolling past it to the
  // results block underneath.
  const restoredResultsRef = useRef(null);
  const sheetExpandedRef = useRef(false);
  const dragRef = useRef({
    active: false, startY: 0, startOffset: 0, lastY: 0, lastT: 0, velocity: 0
  });

  // Helper to keep the expanded ref in sync for pointer event handlers
  const updateSheetExpanded = useCallback((value) => {
    sheetExpandedRef.current = value;
    setSheetExpanded(value);
  }, []);

  // STORY-009: tearing down the questions step is two steps, not one — the
  // answers live in the store rather than in this component, so clearing
  // `showQuestions` on its own would leave the pair behind. Kept together so
  // the three callers (user switch, open, close) cannot drift apart.
  const resetQuestionsFlow = useCallback(() => {
    setShowQuestions(false);
    dispatch(resetAiScannerAnswers());
  }, [dispatch]);

  // STORY (#725): `screen`, `restoredRounds`, and `loadedSessionRef` together
  // represent one thing — "is there a resumable session offered/applied right
  // now" — and must always change as a unit. Centralized here so every
  // caller that means "no, forget about resuming" (user switch, close, a
  // fresh open/start-fresh) shares one place instead of three separately
  // hand-written `setScreen`/`setRestoredRounds`/`loadedSessionRef.current`
  // triples that could drift out of sync.
  const resetSessionScreen = useCallback(() => {
    setScreen('chat');
    setRestoredRounds(null);
    loadedSessionRef.current = null;
  }, []);

  // Reset the overlay when the active user changes (e.g., switching between
  // user sessions on the same device without a full page reload) so state
  // from the previous user's session does not carry over.
  useEffect(() => {
    setIsOpen(false);
    setShowCoachMark(false);
    updateSheetExpanded(false);
    setSheetOffset(null);
    setSheetDragging(false);
    // Unobservable today — `handleOpen` resets too, and the questions only
    // render after it — but the answers are session state like everything
    // else reset here.
    resetQuestionsFlow();
    // STORY (#725): a different user's saved session must not leak into this
    // one's next open — re-decided fresh by the next `handleOpen`.
    resetSessionScreen();
  }, [resetQuestionsFlow, resetSessionScreen, userId, updateSheetExpanded]);

  useEffect(() => {
    if (!isFeatureEnabled) {
      return;
    }

    let rafId = null;

    const updateBarPosition = () => {
      const footer = document.getElementById(MOBILE_FOOTER_ID);
      if (!footer) return;

      const footerRect = footer.getBoundingClientRect();
      if (footerRect.top < window.innerHeight) {
        setBarBottomOverride(window.innerHeight - footerRect.top + BAR_FOOTER_GAP);
      } else {
        setBarBottomOverride(null);
      }
    };

    const handleScroll = () => {
      if (rafId !== null) return;
      rafId = requestAnimationFrame(() => {
        updateBarPosition();
        rafId = null;
      });
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    updateBarPosition();

    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (rafId !== null) cancelAnimationFrame(rafId);
    };
  }, [isFeatureEnabled]);

  // STORY-008: Selection state feeds the live-count derivation further down.
  const [selectedHashtagIds, setSelectedHashtagIds] = useState(() => new Set());

  // STORY-013 (#713): the previous selection, so the change handler can tell an
  // ADD from a REMOVE. Held in a ref rather than read from `selectedHashtagIds`
  // so the callback keeps a stable identity (a new `onSelectionChange` would
  // re-render HashtagChips for nothing).
  const previousSelection = useRef(new Set());

  // STORY-013 (#713): HashtagChips unmounts with the overlay (or the
  // welcome-back screen) and remounts with an empty selection, so the
  // container's view of the selection has to be reset too. Without this,
  // re-tapping a chip that was selected before would read as "not grown" and
  // skip the fetch pass — which also skips the retry for a scan that
  // previously errored.
  const resetHashtagSelection = useCallback(() => {
    previousSelection.current = new Set();
    setSelectedHashtagIds(new Set());
  }, []);

  // STORY (#725): the shared "start from a blank chat" reset — used both by
  // `handleOpen` when there's nothing to resume and by `handleStartFresh`
  // (after clearing the saved session), so the two paths cannot drift apart.
  const openFreshChat = useCallback(() => {
    resetHashtagSelection();
    // STORY-009: a fresh conversation, so neither the questions step nor the
    // previous answers carry over.
    resetQuestionsFlow();
    // Show the coach mark only for users who have not yet seen it on this
    // device. Returning users skip straight to the chat.
    setShowCoachMark(!hasSeen);
    resetSessionScreen();
  }, [hasSeen, resetHashtagSelection, resetQuestionsFlow, resetSessionScreen]);

  const handleOpen = useCallback(() => {
    setIsOpen(true);
    // STORY (#725): decide once, up front, whether there's a session worth
    // offering to resume — the welcome-back screen only ever appears here.
    const savedSession = aiScannerSession.load();
    if (savedSession && savedSession.searchRounds.length > 0) {
      loadedSessionRef.current = savedSession;
      setScreen('welcomeBack');
      return;
    }
    openFreshChat();
  }, [openFreshChat]);

  // STORY (#725): 「新しく始める」 — clear the saved session, then start exactly
  // as `handleOpen` would have if there had been nothing to resume.
  const handleStartFresh = useCallback(() => {
    aiScannerSession.clear();
    openFreshChat();
  }, [openFreshChat]);

  // STORY (#725): 「前回の履歴を見る」 — hand the rounds `handleOpen` already
  // loaded to the chat view for read-only replay (rendering is US4/T018).
  const handleResume = useCallback(() => {
    const savedSession = loadedSessionRef.current;
    loadedSessionRef.current = null;
    resetHashtagSelection();
    resetQuestionsFlow();
    // The onboarding coach mark has nothing to do with a member who has a
    // history to resume — never layer it over the restored history.
    setShowCoachMark(false);
    setRestoredRounds((savedSession && savedSession.searchRounds) || []);
    setScreen('chat');
  }, [resetHashtagSelection, resetQuestionsFlow]);

  // STORY-022: ✕ on the bar hides the bar only — not the chat. Persist the
  // dismissal so the bar stays gone for the month, then drop it from the DOM.
  const handleDismiss = useCallback(() => {
    dismissAiScannerBar();
    setBarDismissed(true);
  }, []);

  const handleClose = useCallback(() => {
    // ✕ during onboarding counts as a completion for "seen" tracking, per
    // FR-007 / Rule 4. If the coach mark was already dismissed this session,
    // don't call markSeen again — the hook itself is idempotent, but skipping
    // the call keeps container behavior explicit and testable.
    if (showCoachMark) {
      markSeen();
    }
    setShowCoachMark(false);
    resetQuestionsFlow();
    setIsOpen(false);
    // STORY (#725): re-evaluate `load()` from scratch on the next open — ✕
    // is not a choice (US5) and must not clear or restore anything.
    resetSessionScreen();
  }, [markSeen, resetQuestionsFlow, resetSessionScreen, showCoachMark]);

  const handleCoachMarkComplete = useCallback(() => {
    markSeen();
    setShowCoachMark(false);
  }, [markSeen]);

  // --- Bottom sheet snap positions (relative to overlay top) ---
  const getSheetSnaps = useCallback(() => {
    const h = sheetRef.current?.parentElement?.clientHeight || window.innerHeight;
    return { open: SHEET_TOP_MARGIN, closed: h - SHEET_PEEK };
  }, []);

  // Tapping the hashtag link programmatically expands the sheet.
  const handleExpandSheet = useCallback(() => {
    updateSheetExpanded(true);
    setSheetOffset(null);
  }, [updateSheetExpanded]);

  // STORY-013 (#713), generalized by STORY-010 (#726): scanName -> the
  // in-flight request promise, so two rapid passes needing the same scan share
  // one Syphon call instead of the second silently relying on the first to
  // land. `shouldFetchScan` deliberately does not consider in-flight state, so
  // this is the only place concurrent duplicates are collapsed. Shared by both
  // passes — the hashtag selection (STORY-007) and the scoring prefetch
  // (STORY-010) — so a scan one has in flight is never requested again by the
  // other.
  const inFlightScans = useRef(new Map());

  // Fire de-duplicated scan requests. Fire-and-forget by design: nothing awaits
  // the pass, so neither caller blocks on it. Success updates `scanData` and
  // failure updates `scanErrors`, and both re-render via useSelector, so no
  // render trigger is needed on settle.
  // User/token validation is handled inside fetchScan (see actions/scan.js).
  const runScanFetchPass = useCallback(
    (scanNames) => {
      const inFlight = inFlightScans.current;

      scanNames.forEach((scanName) => {
        if (inFlight.has(scanName)) return;

        // Freshness lives in the thunk (`shouldFetchScan`), so a scan that is
        // still fresh resolves without touching Syphon.
        const result = dispatch(fetchScanIfNeeded(scanName));
        const settled =
          result && typeof result.then === 'function'
            ? result
            : Promise.resolve(result);
        inFlight.set(scanName, settled);
        settled
          .finally(() => {
            inFlight.delete(scanName);
          })
          // `fetchScan` catches its own failures, so this only fires if
          // something throws inside that catch (e.g.
          // redirectToLoginIfRequired). Swallow it rather than emitting an
          // unhandled rejection.
          .catch(() => {});
      });
    },
    [dispatch]
  );

  // STORY-009: 「NNN 件を表示」 collapses the sheet and starts the question flow.
  //
  // STORY-010: it also starts the scoring-scan prefetch. The tap is the trigger
  // rather than the filter resolving, because each hashtag toggle changes the
  // pool and would re-issue the whole union; by the tap the member has
  // committed to a selection. The pass is not awaited, so the questions open
  // immediately whether the scans have landed, are in flight, or failed — the
  // ranking story falls back to fetch-then-rank in that case.
  //
  // Deliberately fired on EVERY tap rather than once per session: the 60s
  // freshness check in `fetchScanIfNeeded` already makes a repeat tap free, and
  // a session-level guard would suppress the refresh of data that has aged out.
  //
  // Scans the selection already filters on are EXCLUDED, and that exclusion is
  // load-bearing rather than an optimization. `fetchScanIfNeeded` refetches
  // anything past SCAN_STALE_SECONDS, and three scoring scans (`GrowthScore`,
  // `TotalScore`, `NetSalesChange`) are also hashtag scans — so refreshing them
  // here would re-run the filter with newer data and change
  // 「NNN件」 straight after the member tapped that very number. Which hashtags
  // that hit would depend on whether their scans happen to appear in
  // SCORING_SCANS, so the behaviour would differ per hashtag for no reason a
  // member could follow. Nothing is lost by skipping them: a required scan is
  // necessarily already cached, or the count could not have rendered. Making
  // the count refresh on tap is a deliberate product change, not this story.
  const handleShowResults = useCallback(() => {
    updateSheetExpanded(false);
    setSheetOffset(null);
    setShowQuestions(true);

    const alreadyFiltered = new Set(
      getRequiredScans(selectedHashtagIds, HASHTAG_FILTER_CONFIG)
    );
    runScanFetchPass(
      SCORING_SCANS.filter((scanName) => !alreadyFiltered.has(scanName))
    );
  }, [runScanFetchPass, selectedHashtagIds, updateSheetExpanded]);

  // STORY-008: memoized derivation of live count / loading / error.
  //
  // We deliberately do NOT wire this through a reselect selector — reading the
  // store slices with plain `useSelector` and deriving with `useMemo` below
  // keeps the data flow explicit and re-evaluates on every dependency change.
  const scanData = useSelector(
    (state) => (state && state.scans && state.scans.scanData) || null
  );
  const scanErrors = useSelector(
    (state) => (state && state.scans && state.scans.scanErrors) || null
  );

  // STORY-009: watched so the newest message can be scrolled into view when Q2
  // appears. The answers themselves belong to InvestmentQuestions.
  const answeredQ1 = useSelector(
    (state) =>
      (state && state.scans && state.scans.aiScannerAnswers &&
        state.scans.aiScannerAnswers.q1) || null
  );

  // STORY-013: Q2 both reveals the results step and scrolls it into view.
  const answeredQ2 = useSelector(
    (state) =>
      (state && state.scans && state.scans.aiScannerAnswers &&
        state.scans.aiScannerAnswers.q2) || null
  );

  // STORY-011 (#719): inputs for the summarize request. The session id is
  // minted when the questions step opens; the symbol metadata supplies the
  // `name` Syphon requires on every entry.
  const aiScannerSessionId = useSelector(
    (state) => (state && state.scans && state.scans.aiScannerSessionId) || null
  );
  const symbolMetadata = useSelector(
    (state) => (state && state.symbols && state.symbols.data) || null
  );

  // STORY-009: hashtag labels for the header message, in selection order.
  const selectedTagLabels = useMemo(
    () =>
      Array.from(selectedHashtagIds)
        .map((id) => HASHTAG_DEFINITIONS.find((tag) => tag.id === id))
        .filter(Boolean)
        .map((tag) => localize(tag.labelKey)),
    [localize, selectedHashtagIds]
  );

  // Rule 5: keep the latest message visible as the conversation grows.
  useEffect(() => {
    if (!showQuestions) return;
    // Q2 ends the conversation, so the results are read from the top rather
    // than tailed like a question.
    if (resultsRef.current) {
      resultsRef.current.scrollIntoView?.({
        behavior: 'smooth',
        block: 'start'
      });
      return;
    }
    questionsRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'end' });
  }, [answeredQ1, answeredQ2, showQuestions]);

  // STORY (#725): landing on the latest round after 「前回の履歴を見る」 (FR-010).
  // Distinct from the Rule 5 effect above — restoring never sets
  // `showQuestions`, so that effect's guard would otherwise skip this.
  useEffect(() => {
    if (!restoredRounds || restoredRounds.length === 0) return;
    restoredResultsRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
  }, [restoredRounds]);

  // STORY (#725): built once per `restoredRounds` change, not on every
  // render — the restored history is read-only and never changes while an
  // unrelated live-chat re-render happens (e.g. dragging the hashtag sheet
  // fires `setSheetOffset` at pointer-move frequency). Memoizing the
  // elements (not just the data) lets React bail out of reconciling this
  // subtree on those unrelated renders.
  const restoredRoundsElements = useMemo(() => {
    if (!restoredRounds) return null;
    return restoredRounds.map((round, index) => {
      const isLast = index === restoredRounds.length - 1;
      return (
        <div
          key={round.conversationId || index}
          className="ai-scanner-questions-anchor"
          ref={isLast ? restoredResultsRef : undefined}
        >
          <InvestmentQuestions
            readOnly
            overrideAnswers={{ q1: round.q1Answer, q2: round.q2Answer }}
            selectedTags={round.selectedHashtags.map((h) => h.tag)}
            matchCount={round.matchCount}
          />
          <div>
            <ScanResults
              overrideResults={round.results}
              overrideSummaryText={round.aiSummary}
            />
          </div>
        </div>
      );
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restoredRounds]);

  // STORY-007: Fire de-duplicated scan requests when hashtag selection changes.
  const handleHashtagSelectionChange = useCallback(
    (selectedIds) => {
      const nextSelection = new Set(selectedIds);
      const previous = previousSelection.current;
      const selectionGrew = Array.from(nextSelection).some(
        (id) => !previous.has(id)
      );

      previousSelection.current = nextSelection;
      setSelectedHashtagIds(nextSelection);

      // STORY-013 (#713) AC C19820799 / C19820801: deselecting issues no
      // requests at all. The scans required by a smaller selection are always
      // a SUBSET of those required by the larger one, so a removal can never
      // produce a cache miss — the only thing it could trigger is a staleness
      // refetch of data we are already displaying. Skipping the pass entirely
      // keeps those ACs true regardless of how long the user took.
      //
      // Opening the panel is not a separate trigger: HashtagChips unmounts
      // with the overlay and remounts with an empty selection, so the first
      // ADD is always the entry point.
      if (!selectionGrew) return;

      // Fire scan requests immediately (STORY-007 behavior preserved). The
      // whole selection is offered to `fetchScanIfNeeded`, not just the newly
      // required scans, so anything that has gone stale is refreshed too.
      const scanNames = getScansForSelection(nextSelection);
      if (scanNames.length === 0) return;

      runScanFetchPass(scanNames);
    },
    [runScanFetchPass]
  );

  const filterResult = useMemo(() => {
    const empty = {
      count: 0,
      symbols: [],
      isLoading: false,
      hasError: false,
      missingScans: []
    };
    if (!selectedHashtagIds || selectedHashtagIds.size === 0) return empty;

    const data = scanData || {};
    const errors = scanErrors || {};
    const required = getRequiredScans(
      selectedHashtagIds,
      HASHTAG_FILTER_CONFIG
    );

    // STORY-013 (#713): "no usable data yet" is exactly the loading condition,
    // so no separate in-flight check is needed and the count never regresses to
    // `---` for data we already hold — a stale scan keeps showing its previous
    // count while the refresh is in flight.
    //
    // Tests `.symbols`, not just presence, to stay in step with the other two
    // places that ask the same question: `shouldFetchScan` (actions/scan.js)
    // and `shouldFetchField` (desktopScans/ScanResultsSection.js). An entry
    // without symbols must read as "still loading", not as a zero-match result.
    const missing = required.filter((s) => !data[s.toLowerCase()]?.symbols);

    // Errors are only fatal for scans with nothing cached. `shouldFetchScan`
    // refetches a scan once it passes SCAN_STALE_SECONDS, so a scan whose
    // symbols are already on screen can pick up an error from a *refresh* that
    // failed. Checking `required` here instead of `missing` let that discard a
    // count the member was already looking at, and disable the submit button
    // with usable data still in the store — extending the promise in the
    // comment above from "refresh in flight" to "refresh failed".
    if (missing.some((s) => errors[s.toLowerCase()])) {
      return {
        count: null,
        symbols: [],
        isLoading: false,
        hasError: true,
        missingScans: []
      };
    }

    if (missing.length > 0) {
      return {
        count: null,
        symbols: [],
        isLoading: true,
        hasError: false,
        missingScans: missing
      };
    }

    const symbols = filterSelectedHashtags(
      data,
      selectedHashtagIds,
      HASHTAG_FILTER_CONFIG
    );
    return {
      count: symbols.length,
      symbols,
      isLoading: false,
      hasError: false,
      missingScans: []
    };
  }, [scanData, scanErrors, selectedHashtagIds]);

  // STORY-011 (#719): entering the questions step opens a conversation.
  //
  // The symbol names are prefetched HERE rather than at Q2 because
  // `fetchSymbolIfNeeded` batches through a debounce timer and cannot be
  // awaited — firing them while the member reads Q1 leaves several seconds for
  // the batch to land, so `name` is in the store by the time the payload is
  // assembled. A symbol still missing one is dropped by the assembler rather
  // than sent with an empty name, which Syphon rejects.
  useEffect(() => {
    if (!showQuestions) return;
    dispatch(startAiScannerSession());
    // Keyed on `showQuestions` alone: the session belongs to the step, not to
    // the candidate list, which keeps re-deriving as scans land.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, showQuestions]);

  // #736: the answer pairs this round could still end on — every combination
  // consistent with what has been answered so far. Nine before Q1, three once
  // Q1 lands, one once Q2 does.
  //
  // This is what makes the name prefetch below both correct and cheap. Ranking
  // depends on Q2, so the final five are not known until the member answers
  // it — but the summarize request is assembled in that same tick, far too
  // soon for a fetch started then to have landed. Prefetching only the pool's
  // head (which is MarketCap order, not rank order) would have left the actual
  // candidates nameless: dropped from the payload by `buildSymbols`, and
  // rendered as raw tickers on the cards.
  //
  // Enumerating instead of widening keeps it BOUNDED and exact. The union is
  // at most 45 symbols before Q1 and 15 after it, against a batch size of 25 —
  // one or two requests, for a guarantee that whichever answer the member
  // picks, its five candidates already have names.
  const openAnswerPairs = useMemo(() => {
    const codesFor = (questionId, answered) => {
      if (answered) return [answered];
      const question = INVESTMENT_QUESTIONS.find((q) => q.id === questionId);
      return question.options.map((option) => option.code);
    };

    const pairs = [];
    codesFor('q1', answeredQ1).forEach((q1) => {
      codesFor('q2', answeredQ2).forEach((q2) => {
        pairs.push({ q1: q1, q2: q2 });
      });
    });
    return pairs;
  }, [answeredQ1, answeredQ2]);

  // Every symbol that could end up on a card, de-duplicated. Recomputed as the
  // scoring scans land, so a pass that ran against an empty cache is redone
  // with real data rather than leaving the wrong names prefetched.
  const prefetchSymbols = useMemo(() => {
    // Gated on the step being open for the same reason the effect below is:
    // this ranks the pool once per open pair, and every hashtag toggle in the
    // sheet changes `filterResult.symbols`. Before the member has committed to
    // a selection there is nothing to prefetch names for.
    if (!showQuestions) return [];

    const union = new Set();
    openAnswerPairs.forEach((answers) => {
      selectCandidateSymbols(filterResult.symbols, {
        answers: answers,
        scanData: scanData
      }).forEach((symbolId) => union.add(symbolId));
    });
    return Array.from(union);
  }, [filterResult.symbols, openAnswerPairs, scanData, showQuestions]);

  useEffect(() => {
    if (!showQuestions) return;
    // Re-runs when the candidate set changes: a snapshot taken once on entry
    // could name a different five than the payload ends up carrying, and a
    // candidate with no name is skipped outright rather than backfilled — so
    // the two lists have to stay in step or the request silently goes short.
    // `fetchSymbolIfNeeded` is a no-op for anything already held, so the
    // repeats a re-run brings cost nothing.
    prefetchSymbols.forEach((symbolId) => {
      dispatch(fetchSymbolIfNeeded(symbolId));
    });
  }, [dispatch, showQuestions, prefetchSymbols]);

  // #736: the ranked candidates on screen. `ScanResults` is handed these
  // rather than the pool, so the cards and the summarize request are built
  // from one ranking rather than two that could disagree.
  const liveCandidates = useMemo(
    () =>
      selectCandidates(filterResult.symbols, {
        answers: { q1: answeredQ1, q2: answeredQ2 },
        scanData: scanData,
        symbolMetadata: symbolMetadata,
        localize: localize
      }),
    [answeredQ1, answeredQ2, filterResult.symbols, localize, scanData, symbolMetadata]
  );

  // STORY-011 (#719): both questions answered — assemble and send. Also fires
  // when Q1 changes after Q2, re-requesting while an earlier call may be in
  // flight; the reducer drops any result superseded by a newer request.
  //
  // STORY (#724): the round to persist is assembled from the SAME body, so
  // stored hashtags/results always match the summary. `requestScanSummary`
  // persists it only when the summary is accepted (see that thunk); `results`
  // from `body.symbols` reuses the summarizer's name resolution.
  const handleAnswersComplete = useCallback(
    (answers) => {
      // #736: ranked from the `answers` the callback CARRIES, not from
      // `liveCandidates`. The answer that completes the pair was dispatched
      // moments ago and this closure still holds the previous render's store
      // values, so ranking off the memo would score the round on the pair as
      // it stood one answer ago. `selectCandidates` is pure, so re-running it
      // here is just a second call on the same inputs the next render uses —
      // the two agree by construction.
      const symbols = selectCandidateSymbols(filterResult.symbols, {
        answers: answers,
        scanData: scanData
      });
      const body = buildSummarizeRequest({
        sessionId: aiScannerSessionId,
        answers: answers,
        selectedHashtagIds: selectedHashtagIds,
        symbols: symbols,
        scanData: scanData,
        symbolMetadata: symbolMetadata,
        localize: localize
      });
      const round = buildSearchRound(
        body,
        answers,
        aiScannerSessionId,
        filterResult.count
      );
      dispatch(requestScanSummary(body, round));
    },
    [
      aiScannerSessionId,
      dispatch,
      filterResult.symbols,
      localize,
      scanData,
      selectedHashtagIds,
      symbolMetadata,
      filterResult.count
    ]
  );

  // --- Pointer-event drag handlers for the bottom sheet ---
  const handleSheetPointerDown = useCallback((e) => {
    const { open, closed } = getSheetSnaps();
    dragRef.current = {
      active: true,
      startY: e.clientY,
      startOffset: sheetExpandedRef.current ? open : closed,
      lastY: e.clientY,
      lastT: performance.now(),
      velocity: 0,
    };
    setSheetDragging(true);
    if (e.currentTarget.setPointerCapture) {
      e.currentTarget.setPointerCapture(e.pointerId);
    }
  }, [getSheetSnaps]);

  const handleSheetPointerMove = useCallback((e) => {
    if (!dragRef.current.active) return;
    const { open, closed } = getSheetSnaps();
    const now = performance.now();
    const dt = now - dragRef.current.lastT || 16;
    dragRef.current.velocity = (e.clientY - dragRef.current.lastY) / dt;
    dragRef.current.lastY = e.clientY;
    dragRef.current.lastT = now;

    let next = dragRef.current.startOffset + (e.clientY - dragRef.current.startY);
    next = Math.max(open, Math.min(closed, next));
    setSheetOffset(next);
  }, [getSheetSnaps]);

  const handleSheetPointerUp = useCallback((e) => {
    if (!dragRef.current.active) return;
    dragRef.current.active = false;
    setSheetDragging(false);

    const { open, closed } = getSheetSnaps();
    const v = dragRef.current.velocity;
    const traveled = Math.abs(e.clientY - dragRef.current.startY);
    // Compute current position from drag data to avoid stale closure on sheetOffset
    const current = dragRef.current.startOffset + (e.clientY - dragRef.current.startY);
    const wasExpanded = sheetExpandedRef.current;

    let willExpand;
    if (traveled < 6) {
      // Barely moved — treat as a tap and toggle.
      willExpand = !wasExpanded;
    } else if (Math.abs(v) > 0.5) {
      // Fast flick wins regardless of position.
      willExpand = v < 0;
    } else {
      // Slow drag — snap to whichever half is closer.
      willExpand = current < (open + closed) / 2;
    }

    updateSheetExpanded(willExpand);
    setSheetOffset(null);
  }, [getSheetSnaps, updateSheetExpanded]);

  // Escape key collapses the sheet
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => {
      if (e.key === 'Escape' && sheetExpanded) {
        updateSheetExpanded(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, sheetExpanded, updateSheetExpanded]);

  // Move focus into the sheet when it expands so keyboard/screen-reader users
  // are aware the content is available. The sheet container itself receives
  // focus (tabIndex={-1} below).
  useEffect(() => {
    if (sheetExpanded) {
      sheetRef.current?.focus();
    }
  }, [sheetExpanded]);

  if (!isFeatureEnabled) {
    return null;
  }

  // Compute bottom sheet translateY
  const { open: snapOpen, closed: snapClosed } = getSheetSnaps();
  const sheetY = sheetOffset ?? (sheetExpanded ? snapOpen : snapClosed);
  const sheetStyle = {
    transform: `translateY(${sheetY}px)`,
    transition: sheetDragging ? 'none' : 'transform 0.32s cubic-bezier(0.32, 0.72, 0, 1)',
  };

  return (
    <div className="ai-scanner-container">
      {!barDismissed && (
        <div
          className="ai-scanner-bar"
          style={
            barBottomOverride !== null
              ? { bottom: `${barBottomOverride}px` }
              : undefined
          }
        >
          <button
            className="ai-scanner-bar-body"
            type="button"
            onClick={handleOpen}
            aria-label="AIアシスタントを開く"
            aria-expanded={isOpen}
          >
            <span className="ai-scanner-bar-heading">
              <img
                className="ai-scanner-bar-logo"
                src={aiScannerLogo}
                alt=""
                aria-hidden="true"
                width="48"
                height="48"
              />
              <span className="ai-scanner-bar-title">
                {localize('ai.scanner.bar.title')}
              </span>
            </span>
            <span className="ai-scanner-bar-subtitle">
              {localize('ai.scanner.bar.subtitle')}
            </span>
          </button>
          <button
            className="ai-scanner-bar-dismiss"
            type="button"
            onClick={handleDismiss}
            aria-label="閉じる"
          >
            <img
              src={closeIcon}
              alt=""
              aria-hidden="true"
              width="16"
              height="16"
            />
          </button>
        </div>
      )}
      {isOpen && (
        <div
          className={`ai-scanner-overlay${showQuestions ? ' ai-scanner-overlay--questions' : ''
            }`}
          role="dialog"
          aria-modal="true"
          aria-label="AIスキャナー"
        >
          <button
            className="ai-scanner-overlay-close"
            onClick={handleClose}
            aria-label="閉じる"
          >
            <img
              src={closeIcon}
              alt=""
              aria-hidden="true"
              width="16"
              height="16"
            />
          </button>
          {screen === 'welcomeBack' && (
            <WelcomeBackScreen
              onStartFresh={handleStartFresh}
              onResume={handleResume}
            />
          )}
          {screen === 'chat' && (
            <>
              <div className="ai-scanner-overlay-content">
                {/*
              STORY (#725): every round from a resumed session, oldest first,
              read-only — reusing InvestmentQuestions/ScanResults in
              override mode rather than a parallel renderer (FR-008/FR-009).
              `restoredResultsRef` (NOT `resultsRef` — see its declaration)
              lands on the LAST round's own container, so `block: 'start'`
              stops at that round's hashtag-condition header rather than
              scrolling past it to the results card underneath (FR-010).
            */}
                {restoredRoundsElements}
                {!showQuestions && (
                  <>
                    <p className="ai-scanner-overlay-greeting">
                      {localize('ai.scanner.greeting')}
                    </p>
                    <button
                      className="ai-scanner-overlay-hashtag-link"
                      onClick={handleExpandSheet}
                      type="button"
                    >
                      {localize('ai.scanner.hashtag.link')}
                    </button>
                  </>
                )}
                {showQuestions && (
                  <div ref={questionsRef} className="ai-scanner-questions-anchor">
                    <InvestmentQuestions
                      selectedTags={selectedTagLabels}
                      matchCount={filterResult.count}
                      onAnswersComplete={handleAnswersComplete}
                    />
                    {/*
                  Clearing the hashtag selection (or losing the scans to a
                  load/error) empties the filtered set, and the step falls back
                  to the STORY-009 warning on its own. Nothing from the results
                  step should survive that: a confirmation message and a
                  「0 件 見つかりました！」 under a "no matches" warning is a
                  contradiction, and NFR 13 puts the floor at 3 anyway.
                */}
                    {answeredQ1 && answeredQ2 && filterResult.symbols.length > 0 && (
                      <div ref={resultsRef}>
                        {/*
                      Keyed on the hashtag selection so changing the search
                      rebuilds the step and drops the ticked cards: the ticks
                      answered the previous question, and carrying them into a
                      new candidate list would show cards ticked that the
                      member never ticked here. Deliberately NOT keyed on the
                      symbol list — a background scan refresh can change that
                      with no member action, and it must not discard their
                      selection.
                    */}
                        <ScanResults
                          key={Array.from(selectedHashtagIds).sort().join()}
                          candidates={liveCandidates}
                        />
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div
                className={`ai-scanner-overlay-swipe-area${sheetExpanded ? ' ai-scanner-overlay-swipe-area--expanded' : ''
                  }`}
                ref={sheetRef}
                style={{ '--sheet-top-margin': `${SHEET_TOP_MARGIN}px`, ...sheetStyle }}
                tabIndex={-1}
                role={sheetExpanded ? 'region' : undefined}
                aria-labelledby={sheetExpanded ? 'ai-scanner-sheet-title' : undefined}
              >
                <div
                  className="ai-scanner-overlay-swipe-area-handle"
                  onPointerDown={handleSheetPointerDown}
                  onPointerMove={handleSheetPointerMove}
                  onPointerUp={handleSheetPointerUp}
                  onPointerCancel={handleSheetPointerUp}
                  role="button"
                  tabIndex={0}
                  aria-expanded={sheetExpanded}
                  aria-label={sheetExpanded ? localize('button.label.close') : localize('ai.scanner.hashtag.modal.open')}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      updateSheetExpanded(!sheetExpanded);
                    }
                  }}
                >
                  <div className="ai-scanner-overlay-swipe-area-grabber" />
                </div>
                <div
                  className="ai-scanner-overlay-swipe-area-body"
                  aria-hidden={!sheetExpanded}
                  style={{
                    opacity: sheetExpanded || sheetOffset !== null ? 1 : 0,
                    overflowY: 'hidden',
                  }}
                >
                  <div className="ai-scanner-overlay-swipe-area-content">
                    <p id="ai-scanner-sheet-title" className="ai-scanner-overlay-swipe-area-title">
                      {localize('ai.scanner.hashtag.modal.title')}
                    </p>
                    <HashtagChips
                      onSelectionChange={handleHashtagSelectionChange}
                      onShowResults={handleShowResults}
                      matchCount={filterResult.count}
                      isLoading={filterResult.isLoading}
                      hasError={filterResult.hasError}
                    />
                  </div>
                </div>
              </div>
              {showCoachMark && (
                <AiScannerCoachMark onComplete={handleCoachMarkComplete} />
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default AiScannerContainer;
